import { useActiveProject } from '@hooks/useActiveProject';
import { useProjects } from '@hooks/useProjects';
import { cn } from '@lib/utils';
import * as Popover from '@radix-ui/react-popover';
import type {
  ProjectListItem,
  ProjectSubscriptionStatus,
} from '@services/projects.service';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { useState } from 'react';

import { AddNumberModal } from './AddNumberModal';

function displayLabel(p: ProjectListItem): string {
  return p.display_name_witzy ?? p.name;
}

/**
 * Subscription-status dot. emerald = active/trialing, amber = past_due,
 * red = suspended, gray = cancelled or no subscription yet.
 */
function statusDotClass(
  status: ProjectSubscriptionStatus | null | undefined,
): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-emerald-500';
    case 'past_due':
      return 'bg-amber-500';
    case 'suspended':
      return 'bg-rose-500';
    case 'cancelled':
      return 'bg-apple-gray-400';
    default:
      return 'bg-apple-gray-300';
  }
}

function statusLabel(
  status: ProjectSubscriptionStatus | null | undefined,
): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'trialing':
      return 'Trialing';
    case 'past_due':
      return 'Past due';
    case 'suspended':
      return 'Suspended';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'No subscription';
  }
}

function StatusDot({
  status,
  className,
}: {
  status: ProjectSubscriptionStatus | null | undefined;
  className?: string;
}): JSX.Element {
  return (
    <span
      aria-label={`Status: ${statusLabel(status)}`}
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-full',
        statusDotClass(status),
        className,
      )}
    />
  );
}

export function ProjectSwitcher(): JSX.Element {
  const { isReady, selectedProjectId, selectedProject, setSelected } = useActiveProject();
  const { projects } = useProjects();
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Skeleton pill while we wait for the projects query to settle.
  if (!isReady) {
    return (
      <div
        aria-label="Loading projects"
        className="hidden h-9 w-44 animate-pulse rounded-xl bg-apple-gray-100 md:block"
      />
    );
  }

  // Zero projects → show only the CTA.
  if (projects.length === 0) {
    return (
      <>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="hidden h-9 items-center gap-1.5 rounded-xl bg-apple-gray-100 px-3 text-[13px] font-medium text-apple-label transition hover:bg-apple-gray-200 md:inline-flex"
        >
          <Plus size={14} strokeWidth={1.75} />
          Add WhatsApp Number
        </button>
        <AddNumberModal isOpen={addOpen} onClose={() => setAddOpen(false)} />
      </>
    );
  }

  const label = selectedProject ? displayLabel(selectedProject) : 'Select project';

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="hidden h-9 max-w-[220px] items-center gap-1.5 rounded-xl bg-apple-gray-100 px-3 text-[13px] font-medium text-apple-label transition hover:bg-apple-gray-200 md:inline-flex"
            aria-label="Switch project"
          >
            {selectedProject ? (
              <StatusDot status={selectedProject.subscription?.status} />
            ) : null}
            <span className="truncate">{label}</span>
            <ChevronDown size={14} strokeWidth={1.75} className="shrink-0 text-apple-gray-500" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={6}
            align="start"
            className={cn(
              'z-[150] w-72 overflow-hidden rounded-2xl border border-apple-divider bg-white shadow-modal',
              'data-[state=open]:animate-fade-in',
            )}
          >
            <div className="max-h-72 overflow-y-auto py-1.5">
              {projects.map((p) => {
                const active = p.id === selectedProjectId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setSelected(p.id);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] transition',
                      active
                        ? 'bg-apple-gray-100 font-semibold text-apple-label'
                        : 'text-apple-label hover:bg-apple-gray-100',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <StatusDot status={p.subscription?.status} />
                      <span className="min-w-0 truncate">{displayLabel(p)}</span>
                    </span>
                    {active ? (
                      <Check size={14} strokeWidth={2} className="shrink-0 text-emerald-500" />
                    ) : null}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-apple-divider p-1.5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setAddOpen(true);
                }}
                className="flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-medium text-apple-label transition hover:bg-apple-gray-100"
              >
                <Plus size={14} strokeWidth={1.75} />
                Add WhatsApp Number
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <AddNumberModal isOpen={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
}
